// Admin Trainer Management — /admin/trainers
//
// Admin-only via the EXISTING AdminGate pattern (admin_users row
// check). Uses only the deployed RPCs below — no direct table
// writes, no frontend-invented status values:
//   admin_list_trainer_applications(p_status)
//   admin_get_trainer_application(p_application_id)
//   admin_update_trainer_status(p_application_id, p_internal_notes, p_status)
//   admin_assign_trainer_code(p_application_id, p_code, p_discount_pct,
//     p_max_discounted_core_meals, p_minimum_core_meals, p_notes)
//   admin_list_trainer_referral_codes(p_trainer_id, p_include_expired)
//
// Approval and trainer-code issuance are separate backend actions —
// approving sets status/trainer_id/trainer_active only; a code is
// only created by admin_assign_trainer_code. The lifecycle label
// shown here is derived from that real backend data (application
// status + trainer_id + the actual code rows returned by
// admin_list_trainer_referral_codes), never inferred from status
// alone. The discount/meal-count fields exist because
// admin_assign_trainer_code's deployed signature already exposes
// them — they are not hardcoded business rules, just inputs passed
// straight through to that RPC.

import React, { useCallback, useEffect, useState } from 'react';
import { AppShell } from '../../components/AppShell';
import { AdminGate } from '../../components/AdminGate';
import { BrandButton } from '../../components/BrandButton';
import {
  rpcAdminListTrainerApplications,
  rpcAdminGetTrainerApplication,
  rpcAdminUpdateTrainerStatus,
  rpcAdminAssignTrainerCode,
  rpcAdminListTrainerReferralCodes,
  readTrainerApplicationsList,
  readTrainerApplicationDetail,
  readAdminOutcome,
  readTrainerReferralCodesOutcome,
  readTrainerReferralCodesList,
} from '../../lib/trainerAdmin';

const inputStyle = {
  padding: '10px 12px',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius)',
  background: 'var(--paper)',
  color: 'var(--ink)',
  fontSize: '14px',
  width: '100%',
};
const errStyle = { color: 'var(--danger, #b03a2e)' };
const successStyle = { color: 'var(--success, #2e7d32)' };
const warningStyle = { color: 'var(--warning, #b8860b)' };

const FILTERS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'suspended', label: 'Suspended' },
];

// Known admin_update_trainer_status outcomes surfaced with plain
// language — "self_approval_forbidden" is a real backend safeguard,
// not an error to hide.
const STATUS_OUTCOME_COPY = {
  updated: 'Updated.',
  self_approval_forbidden: "You can't change the status of your own trainer application. Ask another admin to review it.",
};

function fmtDate(v) {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return '—'; }
}

// Lifecycle stage is derived from real backend data only:
// application status + whether a trainer record exists + whether
// admin_list_trainer_referral_codes returned a currently-valid code.
function computeLifecycleStage(detail, codes) {
  if (!detail) return null;
  const status = detail.status;
  if (status === 'pending') return { label: 'PENDING REVIEW', tone: warningStyle };
  if (status === 'rejected') return { label: 'REJECTED', tone: errStyle };
  if (status === 'suspended') return { label: 'SUSPENDED', tone: errStyle };
  if (status === 'approved') {
    const hasActiveValidCode = (codes || []).some((c) => c.currently_valid);
    return hasActiveValidCode
      ? { label: 'APPROVED — ACTIVE TRAINER CODE', tone: successStyle }
      : { label: 'APPROVED — TRAINER CODE NOT YET ISSUED', tone: warningStyle };
  }
  return { label: status || 'UNKNOWN', tone: { color: 'var(--ink)' } };
}

// The single most relevant code row to show in the readback card:
// the currently-valid one if any, otherwise the most recently
// issued one (so an expired/inactive code is still visible, not
// hidden), otherwise none.
function pickMostRelevantCode(codes) {
  if (!codes || codes.length === 0) return null;
  const validOne = codes.find((c) => c.currently_valid);
  if (validOne) return validOne;
  const sorted = [...codes].sort((a, b) => new Date(b.valid_from || 0) - new Date(a.valid_from || 0));
  return sorted[0] || null;
}

function codeStateBadge(code) {
  if (!code) return null;
  if (code.currently_valid) return { label: 'ACTIVE / CURRENTLY VALID', tone: successStyle };
  if (code.active === false || code.revoked_at) return { label: 'INACTIVE', tone: errStyle };
  return { label: 'EXPIRED', tone: warningStyle };
}

function AdminTrainersInner() {
  const [filter, setFilter] = useState('');
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const [codes, setCodes] = useState([]);
  const [codesError, setCodesError] = useState('');

  const [internalNotes, setInternalNotes] = useState('');
  const [statusActionBusy, setStatusActionBusy] = useState('');
  const [statusActionError, setStatusActionError] = useState('');
  const [statusActionOk, setStatusActionOk] = useState('');

  const [codeValue, setCodeValue] = useState('');
  const [discountPct, setDiscountPct] = useState('');
  const [maxDiscountedCoreMeals, setMaxDiscountedCoreMeals] = useState('');
  const [minimumCoreMeals, setMinimumCoreMeals] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [assignOk, setAssignOk] = useState(false);

  const loadList = useCallback((status) => {
    setListLoading(true);
    setListError('');
    rpcAdminListTrainerApplications(status || null).then((res) => {
      if (!res.ok) {
        setListError(res.error || 'We could not load trainer applications.');
        setListLoading(false);
        return;
      }
      setList(readTrainerApplicationsList(res.data));
      setListLoading(false);
    });
  }, []);

  useEffect(() => { loadList(filter); }, [filter, loadList]);

  function openDetail(applicationId) {
    setSelectedId(applicationId);
    setDetail(null);
    setCodes([]);
    setCodesError('');
    setDetailError('');
    setStatusActionError('');
    setStatusActionOk('');
    setAssignError('');
    setAssignOk(false);
    setInternalNotes('');
    setCodeValue('');
    setDiscountPct('');
    setMaxDiscountedCoreMeals('');
    setMinimumCoreMeals('');
    setAssignNotes('');
    setDetailLoading(true);
    rpcAdminGetTrainerApplication(applicationId).then((res) => {
      if (!res.ok) {
        setDetailError(res.error || 'We could not load that application.');
        setDetailLoading(false);
        return;
      }
      const row = readTrainerApplicationDetail(res.data);
      setDetail(row);
      setInternalNotes(row?.internal_notes || '');
      setDetailLoading(false);
      if (row?.trainer_id) loadCodes(row.trainer_id);
    });
  }

  function loadCodes(trainerId) {
    setCodesError('');
    rpcAdminListTrainerReferralCodes({ trainerId, includeExpired: true }).then((res) => {
      if (!res.ok) {
        setCodesError(res.error || "We couldn't load this trainer's code history.");
        return;
      }
      const outcome = readTrainerReferralCodesOutcome(res.data);
      if (outcome !== 'ok') { setCodes([]); return; }
      setCodes(readTrainerReferralCodesList(res.data));
    });
  }

  async function handleStatusAction(nextStatus, actionKey) {
    if (!selectedId) return;
    setStatusActionBusy(actionKey || nextStatus);
    setStatusActionError('');
    setStatusActionOk('');
    const res = await rpcAdminUpdateTrainerStatus({
      applicationId: selectedId,
      status: nextStatus,
      internalNotes: internalNotes.trim() || null,
    });
    setStatusActionBusy('');
    if (!res.ok) {
      setStatusActionError(res.error || "We couldn't update that trainer's status.");
      return;
    }
    const outcome = readAdminOutcome(res.data);
    if (outcome === 'self_approval_forbidden') {
      setStatusActionError(STATUS_OUTCOME_COPY.self_approval_forbidden);
      return;
    }
    setStatusActionOk(STATUS_OUTCOME_COPY[outcome] || 'Updated.');
    openDetail(selectedId);
    loadList(filter);
  }

  async function handleAssignCode(e) {
    e?.preventDefault?.();
    if (!selectedId) return;
    setAssignError('');
    setAssignOk(false);
    if (!codeValue.trim()) { setAssignError('Please enter a code.'); return; }
    setAssignBusy(true);
    const res = await rpcAdminAssignTrainerCode({
      applicationId: selectedId,
      code: codeValue.trim().toUpperCase(),
      discountPct: discountPct === '' ? null : Number(discountPct),
      maxDiscountedCoreMeals: maxDiscountedCoreMeals === '' ? null : Number(maxDiscountedCoreMeals),
      minimumCoreMeals: minimumCoreMeals === '' ? null : Number(minimumCoreMeals),
      notes: assignNotes.trim() || null,
    });
    setAssignBusy(false);
    if (!res.ok) {
      setAssignError(res.error || "We couldn't assign that trainer code.");
      return;
    }
    setAssignOk(true);
    openDetail(selectedId);
    loadList(filter);
  }

  const lifecycle = computeLifecycleStage(detail, codes);
  const relevantCode = pickMostRelevantCode(codes);
  const codeBadge = codeStateBadge(relevantCode);

  return (
    <AppShell>
      <div className="wizard-card" data-testid="admin-trainers-page">
        <div className="eyebrow" data-testid="admin-trainers-eyebrow">Admin</div>
        <h1 className="h2 mt-3" data-testid="admin-trainers-title">Trainer applications</h1>

        <div className="mt-6 flex gap-2 flex-wrap" data-testid="admin-trainers-filters">
          {FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              type="button"
              onClick={() => setFilter(f.value)}
              className="btn btn-sm"
              style={{
                background: filter === f.value ? 'var(--ink)' : 'var(--paper)',
                color: filter === f.value ? 'var(--cream)' : 'var(--ink)',
                border: '1px solid var(--line)',
              }}
              data-testid={`admin-trainers-filter-${f.value || 'all'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {listError && !listLoading && (
          <p className="mt-4 text-sm" style={errStyle} data-testid="admin-trainers-list-error">{listError}</p>
        )}
        {listLoading && (
          <p className="mt-4 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="admin-trainers-list-loading">Loading…</p>
        )}

        {!listLoading && !listError && (
          <div className="mt-4 overflow-auto" data-testid="admin-trainers-list">
            {list.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--text-dim)' }} data-testid="admin-trainers-list-empty">No applications for this filter.</p>
            ) : (
              <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--line)' }}>
                    <th className="text-left py-2">Applicant</th>
                    <th className="text-left py-2">Business</th>
                    <th className="text-left py-2">Specialty</th>
                    <th className="text-left py-2">Location</th>
                    <th className="text-left py-2">Requested code</th>
                    <th className="text-left py-2">Submitted</th>
                    <th className="text-left py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((row) => (
                    <tr
                      key={row.application_id}
                      onClick={() => row.application_id && openDetail(row.application_id)}
                      style={{ borderBottom: '1px solid var(--line)', cursor: 'pointer', background: selectedId === row.application_id ? 'var(--paper-2, rgba(0,0,0,0.03))' : 'transparent' }}
                      data-testid={`admin-trainers-row-${row.application_id}`}
                    >
                      <td className="py-2">{[row.first_name, row.last_name].filter(Boolean).join(' ') || '—'}</td>
                      <td className="py-2">{row.business_name || '—'}</td>
                      <td className="py-2">{row.training_specialty || '—'}</td>
                      <td className="py-2">{row.location || '—'}</td>
                      <td className="py-2">{row.requested_trainer_code || '—'}</td>
                      <td className="py-2">{fmtDate(row.submitted_at)}</td>
                      <td className="py-2">{row.status || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {selectedId && (
          <div className="mt-8 p-4" style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--paper)' }} data-testid="admin-trainers-detail">
            {detailLoading && <p className="text-sm" style={{ color: 'var(--text-dim)' }} data-testid="admin-trainers-detail-loading">Loading application…</p>}
            {detailError && !detailLoading && <p className="text-sm" style={errStyle} data-testid="admin-trainers-detail-error">{detailError}</p>}

            {!detailLoading && !detailError && detail && (
              <>
                <h2 className="h3" data-testid="admin-trainers-detail-heading">
                  {[detail.first_name, detail.last_name].filter(Boolean).join(' ') || 'Trainer application'}
                </h2>

                {/* Lifecycle stage — derived from status + trainer_id + real code data */}
                {lifecycle && (
                  <div className="mt-3 eyebrow" style={lifecycle.tone} data-testid="admin-trainers-lifecycle-stage">
                    {lifecycle.label}
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-3 mt-4 text-sm">
                  <div><span style={{ color: 'var(--text-dim)' }}>First name:</span> {detail.first_name || '—'}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Last name:</span> {detail.last_name || '—'}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Email:</span> {detail.email || '—'}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Phone:</span> {detail.phone || '—'}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Business name:</span> {detail.business_name || '—'}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Training specialty:</span> {detail.training_specialty || '—'}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Location:</span> {detail.location || '—'}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Website / social:</span> {detail.website_or_social || '—'}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Certification credentials:</span> {detail.certification_credentials || '—'}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Approximate active clients:</span> {detail.approximate_active_clients ?? '—'}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Requested trainer code:</span> {detail.requested_trainer_code || '—'}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Terms accepted:</span> {detail.program_terms_accepted == null ? '—' : (detail.program_terms_accepted ? 'Yes' : 'No')}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Terms version:</span> {detail.terms_version || '—'}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Submitted date:</span> {fmtDate(detail.submitted_at)}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Application status:</span> {detail.status || '—'}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Approved date:</span> {fmtDate(detail.approved_at)}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Trainer record created:</span> {detail.trainer_id ? 'Yes' : 'No'}</div>
                  <div><span style={{ color: 'var(--text-dim)' }}>Trainer active:</span> {detail.trainer_active == null ? '—' : (detail.trainer_active ? 'Yes' : 'No')}</div>
                </div>
                <div className="mt-3 text-sm">
                  <span style={{ color: 'var(--text-dim)' }}>Bio:</span> {detail.bio || '—'}
                </div>

                {/* Internal notes + status actions */}
                <div className="mt-6">
                  <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Internal notes (admin only)</label>
                  <textarea value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)}
                    rows={3} className="mt-2" style={{ ...inputStyle, resize: 'vertical' }}
                    data-testid="admin-trainers-internal-notes-input" />
                </div>

                <div className="mt-4 flex gap-2 flex-wrap">
                  <BrandButton type="button" size="sm" variant="primary" disabled={!!statusActionBusy}
                    onClick={() => handleStatusAction('approved', 'approve')} data-testid="admin-trainers-approve-btn">
                    {statusActionBusy === 'approve' ? 'Approving…' : 'Approve'}
                  </BrandButton>
                  <BrandButton type="button" size="sm" variant="outline" disabled={!!statusActionBusy}
                    onClick={() => handleStatusAction('rejected', 'reject')} data-testid="admin-trainers-reject-btn">
                    {statusActionBusy === 'reject' ? 'Rejecting…' : 'Reject'}
                  </BrandButton>
                  <BrandButton type="button" size="sm" variant="outline" disabled={!!statusActionBusy}
                    onClick={() => handleStatusAction('suspended', 'suspend')} data-testid="admin-trainers-suspend-btn">
                    {statusActionBusy === 'suspend' ? 'Suspending…' : 'Suspend'}
                  </BrandButton>
                  <BrandButton type="button" size="sm" variant="outline" disabled={!!statusActionBusy}
                    onClick={() => handleStatusAction('approved', 'reactivate')} data-testid="admin-trainers-reactivate-btn">
                    {statusActionBusy === 'reactivate' ? 'Reactivating…' : 'Reactivate'}
                  </BrandButton>
                </div>
                {statusActionError && <p className="mt-3 text-sm" style={errStyle} data-testid="admin-trainers-status-action-error">{statusActionError}</p>}
                {statusActionOk && !statusActionError && <p className="mt-3 text-sm" style={successStyle} data-testid="admin-trainers-status-action-ok">{statusActionOk}</p>}

                {/* Active code readback — admin_list_trainer_referral_codes only */}
                <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--line)' }} data-testid="admin-trainers-code-readback">
                  <h3 className="h3" data-testid="admin-trainers-code-readback-heading">Trainer code configuration</h3>
                  {codesError && <p className="mt-3 text-sm" style={errStyle} data-testid="admin-trainers-code-readback-error">{codesError}</p>}
                  {!codesError && !detail.trainer_id && (
                    <p className="mt-3 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="admin-trainers-code-readback-no-trainer">
                      No trainer record exists yet — approve this application first.
                    </p>
                  )}
                  {!codesError && detail.trainer_id && !relevantCode && (
                    <p className="mt-3 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="admin-trainers-code-readback-none">
                      No trainer code has been issued yet.
                    </p>
                  )}
                  {!codesError && relevantCode && (
                    <>
                      {codeBadge && (
                        <div className="mt-3 eyebrow" style={codeBadge.tone} data-testid="admin-trainers-code-state-badge">{codeBadge.label}</div>
                      )}
                      <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
                        <div><span style={{ color: 'var(--text-dim)' }}>Trainer code:</span> {relevantCode.code || '—'}</div>
                        <div><span style={{ color: 'var(--text-dim)' }}>Discount %:</span> {relevantCode.discount_percent == null ? '—' : `${relevantCode.discount_percent}%`}</div>
                        <div><span style={{ color: 'var(--text-dim)' }}>Minimum core meals:</span> {relevantCode.minimum_core_meals ?? '—'}</div>
                        <div><span style={{ color: 'var(--text-dim)' }}>Maximum discounted core meals:</span> {relevantCode.max_discounted_core_meals ?? '—'}</div>
                        <div><span style={{ color: 'var(--text-dim)' }}>Valid from:</span> {fmtDate(relevantCode.valid_from)}</div>
                        <div><span style={{ color: 'var(--text-dim)' }}>Expiration:</span> {fmtDate(relevantCode.expires_at)}</div>
                        <div><span style={{ color: 'var(--text-dim)' }}>Active:</span> {relevantCode.active == null ? '—' : (relevantCode.active ? 'Yes' : 'No')}</div>
                        <div><span style={{ color: 'var(--text-dim)' }}>Currently valid:</span> {relevantCode.currently_valid == null ? '—' : (relevantCode.currently_valid ? 'Yes' : 'No')}</div>
                        <div><span style={{ color: 'var(--text-dim)' }}>Approved at:</span> {fmtDate(relevantCode.approved_at)}</div>
                      </div>
                    </>
                  )}
                </div>

                {/* Assign trainer code — approved trainers only */}
                {detail.status === 'approved' && (
                  <form onSubmit={handleAssignCode} className="mt-8 pt-6" style={{ borderTop: '1px solid var(--line)' }} data-testid="admin-trainers-assign-form">
                    <h3 className="h3" data-testid="admin-trainers-assign-heading">Assign trainer code</h3>
                    <div className="grid sm:grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Code</label>
                        <input type="text" value={codeValue} onChange={(e) => setCodeValue(e.target.value.toUpperCase())}
                          maxLength={40} className="mt-2" style={{ ...inputStyle, textTransform: 'uppercase' }}
                          data-testid="admin-trainers-assign-code-input" />
                      </div>
                      <div>
                        <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Discount %</label>
                        <input type="number" min="0" max="100" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)}
                          className="mt-2" style={inputStyle} data-testid="admin-trainers-assign-discount-input" />
                      </div>
                      <div>
                        <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Max discounted core meals</label>
                        <input type="number" min="0" value={maxDiscountedCoreMeals} onChange={(e) => setMaxDiscountedCoreMeals(e.target.value)}
                          className="mt-2" style={inputStyle} data-testid="admin-trainers-assign-max-meals-input" />
                      </div>
                      <div>
                        <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Minimum core meals</label>
                        <input type="number" min="0" value={minimumCoreMeals} onChange={(e) => setMinimumCoreMeals(e.target.value)}
                          className="mt-2" style={inputStyle} data-testid="admin-trainers-assign-min-meals-input" />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Notes</label>
                      <textarea value={assignNotes} onChange={(e) => setAssignNotes(e.target.value)}
                        rows={2} className="mt-2" style={{ ...inputStyle, resize: 'vertical' }}
                        data-testid="admin-trainers-assign-notes-input" />
                    </div>
                    {assignError && <p className="mt-3 text-sm" style={errStyle} data-testid="admin-trainers-assign-error">{assignError}</p>}
                    {assignOk && !assignError && <p className="mt-3 text-sm" style={successStyle} data-testid="admin-trainers-assign-ok">Code assigned.</p>}
                    <div className="mt-4">
                      <BrandButton type="submit" size="sm" variant="primary" disabled={assignBusy} data-testid="admin-trainers-assign-submit-btn">
                        {assignBusy ? 'Assigning…' : 'Assign code'}
                      </BrandButton>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}

export default function AdminTrainers() {
  return (
    <AdminGate>
      <AdminTrainersInner />
    </AdminGate>
  );
}
